import { findIndex, includes, without } from "lodash";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { TableFilter } from "../models/table.model";

const GLOBAL_ID = "all"; // For global sites (without company + location id)

export const TABLE_IDS = {
    ACTILITY_DOMAINS: "actility-domains-table",
    ADD_SPACES: "add-spaces-table",
    AUDIT_LOGS_RULES: "audit-logs-rules-table",
    COMPANY_DATA: "company-data-table",
    DEVICE: "device-table",
    ENERGY_AUDIT: "energy-audit-table",
    ENERGY_OVERVIEW: "energy-overview-table",
    ENERGY_RULESET_ASSIGN: "energy-ruleset-assign-table",
    GENERAL_TEMPERATURE: "general-temperature-table",
    GENERAL_TIME: "general-time-table",
    INTEGRATION_ADMINISTRATION: "integration-administration-table",
    INTEGRATION_LOGS: "integration-logs-table",
    GENERAL_LOGS: "general-logs-table",
    SPACE_LOGS: "space-logs-table",
    DEVICE_LOGS: "device-logs-table",
    ENERGY_LOGS: "energy-logs-table",
    LOCATION_DATA: "location-data-table",
    METERS: "meters-table",
    OCCUPANCIES: "occupancies-table",
    SPACES: "spaces-table",
    SPACE_PURPOSES: "space-purposes-table",
    UPDATE_STATUS: "update-status-table",
    USERS: "users-table",
    NOTIFICATION: "notification-table",
    ANNOUNCEMENTS: "announcements-table",
};

export type TableId = (typeof TABLE_IDS)[keyof typeof TABLE_IDS];

// Add initial state for non-global pages
// needs to be set manually
const DEFAULT_VALUES = {
    [TABLE_IDS.DEVICE]: {
        visibleColumns: [
            "configuration",
            "uplinkIntervalSeconds",
            "versionWithUpdate",
            "formattedDescription",
        ],
    },
    [TABLE_IDS.ENERGY_OVERVIEW]: {
        visibleColumns: [],
    },
};

type TableData = {
    locationId: string;
    filters: TableFilter[];
    visibleColumns?: string[];
};

type State = {
    [tableId: string]: TableData[];
};

type Action = {
    getFilters: (params: {
        tableId: string;
        locationId?: string;
    }) => TableFilter[] | undefined;
    setFilter: (params: {
        tableId: string;
        locationId?: string;
        filters: TableFilter[];
    }) => void;
    getVisibleColumns: (params: {
        tableId: string;
        locationId?: string;
    }) => string[] | undefined;
    toggleVisibleColumns: (params: {
        tableId: string;
        locationId?: string;
        column: string;
    }) => void;
};

// Add initial state for global page
const initialState: State = {
    [TABLE_IDS.DEVICE]: [
        {
            locationId: GLOBAL_ID,
            filters: [{ type: "STATUS", values: ["UNASSIGNED"] }],
        },
    ],
};

export const useInternalTableStore = create<State & Action>()(
    persist(
        devtools(
            immer(
                (set, get) =>
                    ({
                        ...initialState,

                        getFilters: ({ tableId, locationId = GLOBAL_ID }) => {
                            const tableData = get()[tableId];
                            if (!tableData) return undefined;

                            const data = tableData.find(
                                (data) => data.locationId === locationId
                            );
                            return data ? data.filters : undefined;
                        },

                        setFilter: ({
                            tableId,
                            locationId = GLOBAL_ID,
                            filters,
                        }) => {
                            set((state) => {
                                if (!state[tableId]) {
                                    state[tableId] = [];
                                }

                                const dataIndex = state[tableId].findIndex(
                                    (data) => data.locationId === locationId
                                );

                                if (dataIndex >= 0) {
                                    state[tableId][dataIndex].filters = filters;
                                } else {
                                    state[tableId].push({
                                        locationId,
                                        filters,
                                        visibleColumns:
                                            DEFAULT_VALUES[tableId]
                                                ?.visibleColumns ?? [],
                                    });
                                }
                            });
                        },

                        getVisibleColumns: ({
                            tableId,
                            locationId = GLOBAL_ID,
                        }) => {
                            const tableData = get()[tableId];
                            if (!tableData) {
                                return (
                                    DEFAULT_VALUES[tableId]?.visibleColumns ??
                                    []
                                );
                            }

                            const data = tableData.find(
                                (data) => data.locationId === locationId
                            );
                            return data
                                ? data.visibleColumns
                                : DEFAULT_VALUES[tableId]?.visibleColumns ?? [];
                        },
                        toggleVisibleColumns: ({
                            tableId,
                            locationId = GLOBAL_ID,
                            column,
                        }) => {
                            set((state) => {
                                const tableData = state[tableId] || [];
                                const dataIndex = findIndex(tableData, {
                                    locationId,
                                });

                                if (dataIndex === -1) {
                                    // Initialize table data if locationId entry doesn't exist
                                    tableData.push({
                                        locationId,
                                        filters: [],
                                        visibleColumns: without(
                                            DEFAULT_VALUES[tableId]
                                                ?.visibleColumns || [],
                                            column
                                        ),
                                    });
                                } else {
                                    const currentColumns =
                                        tableData[dataIndex].visibleColumns ||
                                        [];
                                    tableData[dataIndex].visibleColumns =
                                        includes(currentColumns, column)
                                            ? without(currentColumns, column)
                                            : currentColumns.concat(column);
                                }

                                state[tableId] = tableData;
                            });
                        },
                    } as State & Action)
            )
        ),
        {
            name: "table-storage",
        }
    )
);

export const useTableStore = (tableId: string, locationId: string = "all") => {
    // Accessing the Zustand store
    const visibleColumns = useInternalTableStore((state) =>
        state.getVisibleColumns({ tableId, locationId })
    );

    const filters = useInternalTableStore((state) =>
        state.getFilters({ tableId, locationId })
    );

    const setFilters = useInternalTableStore((state) => state.setFilter);
    const toggleVisibleColumns = useInternalTableStore(
        (state) => state.toggleVisibleColumns
    );

    return {
        visibleColumns,
        filters,
        setFilters: (filters: TableFilter[]) =>
            setFilters({ tableId, locationId, filters }),
        toggleVisibleColumns: (visibleColumn: string) =>
            toggleVisibleColumns({
                tableId,
                locationId,
                column: visibleColumn,
            }),
    };
};
